import { ChatRole, ICharacter, IConversationMessage, resolveGreetingAudioRoute, useGreetingAudioEvents } from "@next-platform/client-common-core";
import { CapabilityKey, useApplicationCapabilities } from "@next-platform/client-common-core/infra/application/capabilities";
import { useEffect } from "react";

import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import useAuth from "@/services/useAuth";

interface IUseGreetingAudioImpressionArgs {
  character: ICharacter | null;
  greetingMessage?: IConversationMessage;
  currentConversationId?: string | null;
}

/**
 * Emits the greeting-audio impression once per greeting view, owned at the chat page level so the
 * dedup baseline survives the greeting bubble remounting when its message gains a server id. Play
 * events stay in the per-bubble `useGreetingAudio` hook.
 */
const useGreetingAudioImpression = ({ character, greetingMessage, currentConversationId }: IUseGreetingAudioImpressionArgs): void => {
  const { hasFeatureFlag: isGreetingPlaybackEnabled } = useFeatureFlag(FEATURE_FLAGS.CUSTOM_VOICE_GREETING_PLAYBACK);
  const ttsCapability = useApplicationCapabilities(CapabilityKey.Tts);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { emitImpression } = useGreetingAudioEvents();

  const isGreetingMessage = greetingMessage?.role === ChatRole.BOT;
  const characterId = character?.id ?? "";
  const characterVoiceId = character?.voice_id ?? null;

  const { route } = resolveGreetingAudioRoute({
    isFeatureEnabled: isGreetingPlaybackEnabled,
    isGreetingMessage,
    isTtsAllowed: ttsCapability?.allowed ?? false,
    isPaywall: ttsCapability?.isPaywall ?? false,
    // Kinde reports isAuthenticated=false while loading; do not treat a signed-in user as a guest yet.
    isAuthenticated: isAuthenticated || isAuthLoading,
    hasCharacterVoice: !!characterVoiceId,
    messageGreetingAudioUrl: greetingMessage?.greeting_audio_url,
  });

  useEffect(() => {
    if (route !== "static") {
      return;
    }
    emitImpression({ characterId, conversationKey: currentConversationId, voiceId: characterVoiceId });
  }, [route, emitImpression, characterId, characterVoiceId, currentConversationId]);
};

export default useGreetingAudioImpression;
